import React, { useCallback, useEffect, useState, useRef } from 'react';
import AssortmentSelection from '../../components/AssortmentSelection/AssortmentSelection';
import NewOrderSummary from './NewOrderSummary';
import './NewOrder.scss';
import { useSelector } from 'react-redux';
import { AppState, dataActions } from 'reducers';
import _ from 'lodash';
import { CartGroup, ISODPackageData, ISODProduct, ImportedProduct, OrderStep } from 'lib/types';
import { getImportedProductsInfo, getOrderById, loadSessionCart, setSessionCart, resetIsodCart } from 'lib/communication/orders';
import { useHistory } from 'react-router-dom';
import store from 'store';
import { UnregisterCallback } from 'history';
import { ISOD_STORAGE_KEY } from 'containers/ISOD/ISOD';
import Notification from '../../components/AssortmentSelection/Notification';
import useCallbackState from 'lib/hooks/useCallbackState';
import { getUserDeliveryAddresses } from 'lib/communication/userInfo';
import { Modal } from 'react-bootstrap';
import ButtonComponent from 'components/ButtonComponent';
import { cartMethods } from 'business_logic';
import { useDevice } from 'lib/hooks/useDevice';
import { GA_checkoutStarted } from 'lib/gtm-utils';

const STEPS = 3;

export type NewOrderState = {
    editedOrderId: string | null;
    copiedOrderId: string | null;
    appendedOrderId: string | null;
    step: number | null;
};

interface cartResetProps {
    cartReset: boolean;
    setCartReset: (value: boolean) => void;
}

const NewOrder = (props: cartResetProps) => {
    const history = useHistory<NewOrderState>();
    const editedOrderId = history.location.state ? history.location.state.editedOrderId : null;
    const copiedOrderId = history.location.state ? history.location.state.copiedOrderId : null;
    const appendedOrderId = history.location.state ? history.location.state.appendedOrderId : null;

    const isDesktop = useDevice();
    const edit = useSelector((state: AppState) => state.data.orders.edit);
    const append = useSelector((state: AppState) => state.data.orders.append);
    const detailedOrder = useSelector((state: AppState) => state.data.orders.detailedOrder);
    const localCart = useSelector((state: AppState) => state.data.orders.localCart);
    const localOrEditCart = useSelector((state: AppState) => (state.data.orders.localCart));
    const isFetching = useSelector((state: AppState) => state.data.orders.props.isFetching);
    const importsWithoutProperUnit = useSelector((state: AppState) => state.data.orders.importsWithoutProperUnit);

    const [step, setStep] = useState(history.location.state?.step ?? (edit ? OrderStep.OrderSummary : OrderStep.SelectAssortmentProducts));

    const [refreshedPage, setRefreshedPage] = useState<boolean>(false);

    const [isloadedCart, setIsLoadedCart] = useState<boolean>(false);
    const unblock = useRef<UnregisterCallback>();

    const [isCancelModeOn, setIsCancelModeOn] = useCallbackState<boolean>(false);

    const fetchOrdersFromSessionStorage = useCallback(async () => {
        const assortment: ImportedProduct[] = [];
        let mergeCart: boolean | undefined;
        if (ISOD_STORAGE_KEY in sessionStorage) {
            const parsed = JSON.parse(sessionStorage.getItem(ISOD_STORAGE_KEY)!) as ISODPackageData[];
            mergeCart = !!parsed[0].scalKoszyk;
            if (!parsed[0].scalKoszyk) {
                resetIsodCart();
            }
            _.forEach(parsed, (data: ISODPackageData) => {
                const isodAsortyment: ImportedProduct[] = _.map(data.asortyment, (a: ISODProduct) => ({
                    symKar: a.symkar,
                    quantity: a.ilosc,
                    dimension: parseFloat(a.dlugosc),
                    unit: a.jm,
                    additionsSelected: data.additions,
                    systemData: null,
                    cartGroup: CartGroup.isod
                }));
                assortment.push(...isodAsortyment);
            });
            sessionStorage.removeItem(ISOD_STORAGE_KEY);
        }
        if (assortment.length > 0) {
            await getImportedProductsInfo(assortment, mergeCart);
            const updatedCart = store.getState().data.orders.localCart;
            setSessionCart(updatedCart, cartMethods.getLocalCartSummaryPrice(updatedCart));
        }
    }, []);

    const goToAssortmentSelection = () => {
        history.push(`${process.env.PUBLIC_URL}/new-order`, {
            ...history.location.state,
            step: OrderStep.SelectAssortmentProducts
        });
    };

    const goToSummary = () => {
        history.push(`${process.env.PUBLIC_URL}/new-order`, {
            ...history.location.state,
            step: OrderStep.OrderSummary
        });

        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const goToOrderForm = () => {
        GA_checkoutStarted(localCart);
        history.push(`${process.env.PUBLIC_URL}/new-order`, {
            ...history.location.state,
            step: OrderStep.SummaryForm
        });
    };

    const completeAction = () => {
        store.dispatch(dataActions.resetOrdersList());
        if (unblock.current) unblock.current();
        history.push(`${process.env.PUBLIC_URL}/orders`);
        if (!isDesktop) window.scrollTo(0, 0);
        store.dispatch(dataActions.setEdit(false));
        store.dispatch(dataActions.setAppend(false));
    };

    useEffect(() => {
        setStep(history.location.state?.step ?? (edit ? OrderStep.OrderSummary : OrderStep.SelectAssortmentProducts));
    }, [history.location.state]);

    useEffect(() => {
        if (history.action === 'POP' && (appendedOrderId || editedOrderId)) {
            setRefreshedPage(true);
        }
    }, []);

    useEffect(() => {
        getUserDeliveryAddresses(); // fetch addresses early so they are ready when placing an order
    }, []);

    useEffect(() => {
        if (!isloadedCart) {
            return;
        }
        setSessionCart(localCart, cartMethods.getLocalCartSummaryPrice(localCart));
    }, [localCart]);

    // map content of the localCart to format readable by CartTable component and with calculated prices
    useEffect(() => {
        store.dispatch(dataActions.setCartTableContent(cartMethods.mapLocalCartToCartTableContent(localOrEditCart)));
    }, [localOrEditCart]);

    useEffect(() => {
        unblock.current = history.block((location) => {
            if (isCancelModeOn) return;
            const msg = location.pathname === `${process.env.PUBLIC_URL}/isod` ? 'Uwaga, przejście do aplikacji ISOD spowoduję utratę dotychczasowych zmian dokonanych przy edycji zamówienia!' : (appendedOrderId ? 'Uwaga, opuszczenie strony spowoduje utracenie zmian i wyjście z trybu domówienia do zamówienia!' : 'Uwaga, opuszczenie strony spowoduje utracenie zmian i wyjście z trybu edycji zamówienia!');
            if ((editedOrderId || appendedOrderId) && location.pathname !== `${process.env.PUBLIC_URL}/new-order` && !window.confirm(msg)) {
                return false;
            }
        });
    }, [isCancelModeOn]);

    useEffect((): any => {
        if (editedOrderId) {
            window.onbeforeunload = (event: Event) => {
                const e = event || window.event;
                e.preventDefault();
                return '';
            };

            setStep(OrderStep.OrderSummary);
            store.dispatch(dataActions.setEdit(true));
            store.dispatch(dataActions.setCartFetching(true));
            getOrderById(editedOrderId, true, null).then((result) => {
                if (result === false) {
                    store.dispatch(dataActions.setIsErrorGettingDetailedOrder(true));
                    setIsCancelModeOn(true, () => history.goBack());
                } else {
                    store.dispatch(dataActions.setDetailedOrder(result));
                    fetchOrdersFromSessionStorage().then(() => {
                        store.dispatch(dataActions.setCartFetching(false));
                    });
                }
            });
        } else if (copiedOrderId) {
            setStep(OrderStep.OrderSummary);
            store.dispatch(dataActions.setCartFetching(true));
            getOrderById(copiedOrderId, true, 'order_copy').then((result) => {
                store.dispatch(dataActions.setCartFetching(false));
                setIsLoadedCart(true);
                if (result === false) {
                    store.dispatch(dataActions.setIsErrorGettingDetailedOrder(true));
                    setIsCancelModeOn(true, () => history.goBack());
                } else {
                    store.dispatch(dataActions.setDetailedOrder(result));
                    history.replace({
                        pathname: `${process.env.PUBLIC_URL}/new-order`,
                        state: {
                            ...(typeof history.location.state === 'object' ? history.location.state : {} as NewOrderState),
                            copiedOrderId: null
                        }
                    });
                }
            });
        } else if (appendedOrderId) {
            window.onbeforeunload = (event: Event) => {
                const e = event || window.event;
                e.preventDefault();
                return '';
            };

            store.dispatch(dataActions.setAppend(true));
            store.dispatch(dataActions.setCartFetching(true));
            getOrderById(appendedOrderId, true, 'order_append').then((result) => {
                store.dispatch(dataActions.setCartFetching(false));
                setIsLoadedCart(true);
                if (result === false) {
                    store.dispatch(dataActions.setIsErrorGettingDetailedOrder(true));
                    setIsCancelModeOn(true, () => history.goBack());
                } else {
                    store.dispatch(dataActions.setDetailedOrder(result));
                    store.dispatch(dataActions.setAppendNagIdSource(appendedOrderId));
                    history.replace({
                        pathname: `${process.env.PUBLIC_URL}/new-order`,
                        state: {
                            ...(typeof history.location.state === 'object' ? history.location.state : {} as NewOrderState)
                        }
                    });
                }
            });
        } else {
            store.dispatch(dataActions.setCartFetching(true));
            loadSessionCart().then((result) => {
                if (result) {
                    fetchOrdersFromSessionStorage().then(() => {
                        store.dispatch(dataActions.setCartFetching(false));
                        setIsLoadedCart(true);
                    });
                }
            });
        }

        // stop the listener when component unmounts
        return () => {
            window.onbeforeunload = null;
            if (unblock.current) unblock.current();
        };
    }, []);

    return (
        <>
            <div className='NewOrder-container'>
                <Notification />
                <div
                    className='NewOrder-steps-and-edit-mode'
                    style={isDesktop ? (
                        step === OrderStep.SelectAssortmentProducts ? { paddingBottom: '4px' } :
                            undefined) : step === OrderStep.SelectAssortmentProducts ? { paddingBottom: '4px' } : undefined}
                >
                    {(!edit && !append) || isFetching ? <div></div> : <></>}
                    {edit && !isFetching ?
                        <div className={`NewOrder-edit-mode-sign${isDesktop ? ' NewOrder-edit-mode-sign-desktop' : ''}`}>
                            EDYCJA ZAMOWIENIA:
                            {' '}
                            <b>{detailedOrder.orderNumber}</b>
                        </div> : <></>}
                    {append && !isFetching ?
                        <div className={`NewOrder-append-mode-sign${isDesktop ? ' NewOrder-append-mode-sign-desktop' : ''}`}>
                            WYSTAWIASZ DOMÓWIENIE DO ZAMÓWIENIA:{' '}<b>{detailedOrder.orderNumber}</b>
                        </div> : <></>}
                    <div className='NewOrder-steps'>
                        Krok
                        {' '}
                        {step}
                        {' '}
                        z
                        {' '}
                        {STEPS}
                    </div>
                </div>
                {step === OrderStep.SelectAssortmentProducts ?
                    <AssortmentSelection goToSummary={() => goToSummary()} cartReset={props.cartReset} setCartReset={props.setCartReset} loadedCart={isloadedCart} /> :
                    <NewOrderSummary isCancelModeOn={isCancelModeOn} setIsCancelModeOn={setIsCancelModeOn} goToAssortmentSelection={() => goToAssortmentSelection()} goToSummary={() => goToSummary()} goToOrderForm={() => goToOrderForm()} detailedOrder={detailedOrder} completeAction={completeAction} />}
            </div>
            <Modal show={refreshedPage} onHide={() => setRefreshedPage(false)} backdrop='static'>
                <Modal.Header className='NewOrder-modal-header' />
                <Modal.Body>{`Po odświeżeniu strony zmiany nie zostały zapisane`}</Modal.Body>
                <Modal.Footer className='NewOrder-modal-footer one-button'>
                    <ButtonComponent variant='primary' text='Kontynuuj' brandColoredButton={false} onClick={() => setRefreshedPage(false)} />
                </Modal.Footer>
            </Modal>
            <Modal show={importsWithoutProperUnit.length > 0 && props.cartReset !== true} onHide={() => store.dispatch(dataActions.setImportsWithoutProperUnit([]))} backdrop='static'>
                <Modal.Header className='NewOrder-modal-header'>
                    <Modal.Title>Błąd przy wczytywaniu produktów do koszyka</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {`Nie udało się wczytać części produktów:`}
                    <div style={{ marginLeft: '15px' }}>
                        {
                            importsWithoutProperUnit.map((importedProduct) => (
                                <li><b>{importedProduct.description}</b>{`, ${importedProduct.quantity} ${importedProduct.unit}`}</li>
                            ))
                        }
                    </div>

                </Modal.Body>
                <Modal.Footer className='NewOrder-modal-footer one-button'>
                    <ButtonComponent variant='primary' text='Kontynuuj' brandColoredButton={false} onClick={() => store.dispatch(dataActions.setImportsWithoutProperUnit([]))} />
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default NewOrder;
